package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.FIGURE_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@InitRoute
fun initTheRockCycle(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** The three rock types, and the process that makes each one. */
private val rockCycleTypes = listOf(
    "Igneous" to "Formed when magma or lava cools and solidifies. Basalt, granite, and obsidian are all igneous."
    , "Sedimentary" to "Formed when sediment — sand, silt, clay, shells — is deposited, compacted, and cemented over time. Often layered, and the only type that commonly holds fossils. Sandstone, shale, and limestone are examples."
    , "Metamorphic" to "Formed when existing rock is changed by heat, pressure, or chemically active fluids without melting. Often banded or foliated. Gneiss, marble, and slate are examples."
)

/** The processes that move rock from one type to another, in the order they appear around the cycle. */
private val rockCycleProcesses = listOf(
    "Cooling & Solidification" to "Magma underground or lava at the surface cools and crystallizes into igneous rock."
    , "Weathering & Erosion" to "Wind, water, ice, and living things break rock at the surface into sediment and carry it away."
    , "Compaction & Cementation" to "Buried sediment is squeezed by the weight above it, and minerals in groundwater glue the grains together into sedimentary rock."
    , "Heat & Pressure" to "Deep burial or colliding plates bake and squeeze rock until its minerals recrystallize, turning it metamorphic."
    , "Melting" to "Pushed deep enough, any rock melts into magma, and the cycle starts over."
)

/** Why the cycle isn't a one-way circle. */
private val rockCycleShortcuts = listOf(
    "Any rock can weather" to "Igneous, sedimentary, and metamorphic rock exposed at the surface all break down into sediment."
    , "Any rock can be metamorphosed" to "Heat and pressure change igneous and sedimentary rock — and can change metamorphic rock again."
    , "Any rock can melt" to "It doesn't have to become metamorphic first; sedimentary rock carried deep in a subduction zone can melt directly."
)

@Composable
private fun RockCycleFigure(src: String, alt: String, caption: String) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = src
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
    }
}

@Composable
private fun RockCycleSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun RockCycleList(items: List<Pair<String, String>>, separator: String) {
    Ul(PageListStyle.toAttrs()) {
        items.forEach { (name, description) ->
            Li {
                B { Text(name) }
                Text("$separator$description")
            }
        }
    }
}

@Composable
private fun TheRockCycleHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "The Rock Cycle: Same Earth, New Rocks"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TheRockCycleHero() {

    TheRockCycleHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/therockcycle/the_rock_cycle.png"
                    , alt = "Diagram of the rock cycle linking igneous, sedimentary, and metamorphic rock"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("The rock cycle — rocks are formed, broken down, changed, and reformed.") } }
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Rocks are not permanent. The mountain you stand on today may be sand on a beach in a few million years, and that sand may one day be buried, squeezed, and melted into something new. The rock cycle is the name for this slow, never-ending recycling of Earth's crust.")
            }

            RockCycleSectionTitle("What Is the Rock Cycle?")
            P {
                Text("The rock cycle describes how the three major rock types — igneous, sedimentary, and metamorphic — transform into one another over geologic time. The material stays the same; what changes is the process acting on it. Heat from Earth's interior drives melting and metamorphism, while the sun, weather, and gravity drive weathering and erosion at the surface.")
            }
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        RockCycleSectionTitle("The Three Types of Rocks")
        RockCycleList(rockCycleTypes, " — ")

        RockCycleFigure(
            src = "/images/typesof/therockcycle/the_three_types_of_rocks.png"
            , alt = "Chart of the three types of rocks with examples of each"
            , caption = "The three types of rocks — same materials, different processes."
        )

        RockCycleSectionTitle("The Key Processes")
        P {
            Text("Five processes keep rock moving around the cycle:")
        }
        RockCycleList(rockCycleProcesses, ": ")

        RockCycleSectionTitle("Not Just a Circle")
        P {
            Text("Diagrams draw the rock cycle as a neat loop, but real rock can take shortcuts in almost any direction:")
        }
        RockCycleList(rockCycleShortcuts, ": ")

        RockCycleSectionTitle("How Long Does It Take?")
        P {
            Text("Some steps are fast — lava can harden into basalt in hours. Most are slow. Turning a mountain into sediment, burying that sediment deep enough to become rock, and lifting it back to the surface usually takes millions to hundreds of millions of years. Plate tectonics is the engine that keeps it all going, carrying rock down at subduction zones and pushing it up into new mountain ranges.")
        }

        RockCycleSectionTitle("For Rockhounds")
        P {
            Text("Knowing the cycle helps you read what you pick up. A rounded pebble in a streambed has already been weathered and tumbled; a layered sandstone was once loose sand; a sparkly schist was once mud baked deep underground. Every rock is a snapshot of one stop on the cycle.")
        }

        RockCycleSectionTitle("In Short")
        P {
            Text("Different rocks, same cycle, one Earth. Igneous, sedimentary, and metamorphic rocks are not separate worlds but stages in a continuous process that has been reshaping our planet for billions of years.")
        }
    }
}

@Page("/types_of_rocks/the_rock_cycle")
@Layout(".components.layouts.PageLayout")
@Composable
fun TheRockCyclePage() {
    TheRockCycleHero()
}
